import React, { useState } from "react";
import { toast } from "sonner";
import { Users, UserPlus, Trash2, Building } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useDeliverable } from "@/hooks/useDeliverable";
import { PageHeader } from "@/components/PageParts";
import { Button, Card, Input, Badge, Empty } from "@/components/ui";

export default function Firm() {
  const { lang } = useAuth();
  const isAr = lang === "ar";
  const { data, loading, reload } = useDeliverable(() => api.myFirm().catch(() => null), []);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(null);

  const firm = data?.firm;
  const isOwner = data?.my_role === "owner" || data?.my_role === "admin";

  async function create() {
    if (!name) return;
    setBusy("create");
    try { await api.createFirm({ name }); toast.success(isAr ? "تم إنشاء المكتب" : "Firm created"); reload(); }
    catch (e) { toast.error(String(e.message || e)); } finally { setBusy(null); }
  }
  async function invite() {
    if (!email) return;
    setBusy("invite");
    try { await api.inviteMember(email.trim().toLowerCase()); setEmail(""); toast.success(isAr ? "تم إرسال الدعوة" : "Invite sent"); reload(); }
    catch (e) { toast.error(String(e.message || e)); } finally { setBusy(null); }
  }

  if (loading) return <div className="px-4 py-6" />;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <PageHeader eyebrow={isAr ? "المكتب" : "Firm"} title={isAr ? "المكتب الهندسي" : "Engineering firm"} right={firm && <Badge tone="primary">{data.members?.length || 0} {isAr ? "عضو" : "members"}</Badge>} />
      {!firm ? (
        <Card>
          <Empty icon={Building} title={isAr ? "لست في أي مكتب بعد" : "You're not in a firm yet"} hint={isAr ? "أنشئ مكتباً متعدد المقاعد لمشاركة المشاريع مع فريقك." : "Create a multi-seat firm to share projects with your team."} />
          <div className="mt-4 flex gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={isAr ? "اسم المكتب" : "Firm name"} className="flex-1" data-testid="firm-name" />
            <Button loading={busy === "create"} onClick={create} data-testid="create-firm-btn">{isAr ? "إنشاء" : "Create"}</Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          <Card>
            <div className="flex items-center gap-2 text-lg font-black"><Users className="text-primary" size={20} /> {firm.name}</div>
            <p className="text-xs text-muted-foreground">{isAr ? "دورك" : "Your role"}: {data.my_role} · {isAr ? "المقاعد" : "seats"}: {data.members?.length || 0}/{firm.max_seats || "—"}</p>
          </Card>

          {isOwner && (
            <Card>
              <p className="mb-3 flex items-center gap-2 text-sm font-black"><UserPlus size={16} /> {isAr ? "دعوة عضو" : "Invite member"}</p>
              <div className="flex gap-2">
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@firm.com" className="flex-1" data-testid="invite-email" />
                <Button loading={busy === "invite"} onClick={invite} data-testid="invite-btn">{isAr ? "دعوة" : "Invite"}</Button>
              </div>
            </Card>
          )}

          <Card>
            <p className="mb-3 text-sm font-black">{isAr ? "الأعضاء" : "Members"}</p>
            <div className="space-y-2">
              {(data.members || []).map((m) => (
                <div key={m.user_id || m.id} className="flex items-center justify-between rounded-lg bg-black/20 px-3 py-2 text-sm">
                  <div><div className="font-bold">{m.full_name || m.email}</div><div className="text-xs text-muted-foreground">{m.role}</div></div>
                  {isOwner && m.role !== "owner" && (
                    <button onClick={async () => { try { await api.removeFirmMember(m.user_id || m.id); reload(); } catch (e) { toast.error(String(e.message || e)); } }} className="text-red-400"><Trash2 size={15} /></button>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {(data.invitations || []).length > 0 && (
            <Card>
              <p className="mb-3 text-sm font-black">{isAr ? "الدعوات المعلّقة" : "Pending invitations"}</p>
              <div className="space-y-2">
                {data.invitations.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between rounded-lg bg-black/20 px-3 py-2 text-sm">
                    <span>{inv.email}</span>
                    {isOwner && <button onClick={async () => { try { await api.revokeInvitation(inv.id); reload(); } catch (e) { toast.error(String(e.message || e)); } }} className="text-xs font-bold text-red-400">{isAr ? "إلغاء" : "Revoke"}</button>}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
